import React, { useState, useEffect, useCallback } from 'react';
import BarraFiltros, { SinResultados } from './BarraFiltros';
import {
  createTheme, ThemeProvider, AppBar, Toolbar, Typography, TextField,
  CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Dialog, DialogActions, DialogContent, DialogTitle,
  Box, MenuItem, Select, InputLabel, FormControl, Button
} from '@mui/material';
import Swal from 'sweetalert2';
import { styled } from '@mui/system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';

// Configuración
const API_URL = process.env.REACT_APP_API_URL || 'https://api.hmsaa.com.mx:5001/api';
// Las fotos de usuario se sirven desde /uploads (sin /api); esa ruta es publica para <img>
const UPLOADS_URL = API_URL.replace(/\/api\/?$/, '');
const JWT_TOKEN_KEY = 'access_token';

// Tema y estilos
const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#dc004e' },
  },
});

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: 'bold',
}));

const UsuariosPage = () => {
  // Estados
  const [usuarios, setUsuarios] = useState([]);
  const [search, setSearch] = useState('');
  const [rolFiltro, setRolFiltro] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUsuario, setCurrentUsuario] = useState(null);
  const [newUsuario, setNewUsuario] = useState({
    nombre: '', correo: '', contrasena: '', es_admin: false, imagen: null,
  });
  const [loading, setLoading] = useState({ data: false, action: false });
  const [errors, setErrors] = useState({});

  // Validaciones
  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!newUsuario.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!newUsuario.correo.trim()) newErrors.correo = 'El correo es requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newUsuario.correo)) {
      newErrors.correo = 'Formato de correo inválido';
    }

    if (!editMode && !newUsuario.contrasena) newErrors.contrasena = 'La contraseña es requerida';
    else if (newUsuario.contrasena && newUsuario.contrasena.length < 6)
      newErrors.contrasena = 'La contraseña debe tener al menos 6 caracteres';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [newUsuario, editMode]);

  // Obtener usuarios
  const fetchUsuarios = useCallback(async () => {
    setLoading(prev => ({ ...prev, data: true }));
    try {
      const token = localStorage.getItem(JWT_TOKEN_KEY);
      const res = await fetch(`${API_URL}/usuarios`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });

      if (res.status === 401) {
        localStorage.removeItem(JWT_TOKEN_KEY);
        Swal.fire('Sesión expirada', 'Por favor inicie sesión nuevamente', 'error');
        return;
      }

      if (!res.ok) throw new Error('Error al cargar usuarios');
      const data = await res.json();
      setUsuarios(data);
    } catch (err) {
      console.error('Error:', err);
      Swal.fire('Error', 'No se pudieron cargar los usuarios', 'error');
    } finally {
      setLoading(prev => ({ ...prev, data: false }));
    }
  }, []);

  // Guardar usuario (POST/PUT con FormData)
  const handleSaveUsuario = async () => {
    if (!validateForm()) return;

    setLoading(prev => ({ ...prev, action: true }));
    try {
      const token = localStorage.getItem(JWT_TOKEN_KEY);
      const formData = new FormData();
      formData.append('nombre', newUsuario.nombre.trim());
      formData.append('correo', newUsuario.correo.trim());
      formData.append('es_admin', newUsuario.es_admin);
      if (newUsuario.contrasena) formData.append('contrasena', newUsuario.contrasena);
      if (newUsuario.imagen) formData.append('imagen', newUsuario.imagen);

      const url = editMode ? `${API_URL}/usuarios/${currentUsuario.id_usuario}` : `${API_URL}/usuarios`;
      const method = editMode ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}` }, // NO Content-Type, browser lo pone con boundary
        body: formData
      });

      if (res.status === 401) {
        localStorage.removeItem(JWT_TOKEN_KEY);
        Swal.fire('Sesión expirada', 'Por favor inicie sesión nuevamente', 'error');
        return;
      }

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Error al guardar usuario');
      }

      const savedUsuario = await res.json();
      // 🔥 ACTUALIZAR TOKEN SI VIENE UNO NUEVO
      if (savedUsuario.token) {
  localStorage.setItem('access_token', savedUsuario.token);
  window.dispatchEvent(new Event('storage'));
}


      Swal.fire('Éxito', 'Usuario guardado correctamente', 'success');

      if (editMode) setUsuarios(prev => prev.map(u => u.id_usuario === savedUsuario.id_usuario ? savedUsuario : u));
      else setUsuarios(prev => [...prev, savedUsuario]);

      handleCloseDialog();
    } catch (err) {
      console.error('Error:', err);
      Swal.fire('Error', err.message, 'error');
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  // Eliminar usuario
  const handleDeleteUsuario = async (id_usuario) => {
    const result = await Swal.fire({
      title: '¿Está seguro?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      setLoading(prev => ({ ...prev, action: true }));
      try {
        const token = localStorage.getItem(JWT_TOKEN_KEY);
        const res = await fetch(`${API_URL}/usuarios/${id_usuario}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
        });

        if (res.status === 401) {
          localStorage.removeItem(JWT_TOKEN_KEY);
          Swal.fire('Sesión expirada', 'Por favor inicie sesión nuevamente', 'error');
          return;
        }

        if (!res.ok) throw new Error('Error al eliminar usuario');
        setUsuarios(prev => prev.filter(u => u.id_usuario !== id_usuario));
        Swal.fire('Eliminado', 'Usuario eliminado correctamente', 'success');
      } catch (err) {
        console.error('Error:', err);
        Swal.fire('Error', 'No se pudo eliminar el usuario', 'error');
      } finally {
        setLoading(prev => ({ ...prev, action: false }));
      }
    }
  };

  // Manejo del diálogo
  const handleOpenDialog = (usuario = null) => {
    if (usuario) {
      setEditMode(true);
      setCurrentUsuario(usuario);
      setNewUsuario({
        nombre: usuario.nombre,
        correo: usuario.correo,
        contrasena: '',
        es_admin: usuario.es_admin,
        imagen: null
      });
    } else {
      setEditMode(false);
      setCurrentUsuario(null);
      setNewUsuario({ nombre: '', correo: '', contrasena: '', es_admin: false, imagen: null });
    }
    setErrors({});
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setNewUsuario(prev => ({ ...prev, contrasena: '', imagen: null }));
  };

  // Filtro de usuarios con debounce
  const [filteredUsuarios, setFilteredUsuarios] = useState([]);
  useEffect(() => {
    const timer = setTimeout(() => {
      const termino = search.trim().toLowerCase();
      setFilteredUsuarios(
        usuarios.filter(u =>
          (rolFiltro === '' || (rolFiltro === 'admin') === Boolean(u.es_admin)) &&
          [u.nombre, u.correo].some(v => (v ?? '').toString().toLowerCase().includes(termino))
        )
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [usuarios, search, rolFiltro]);

  const limpiarFiltros = () => {
    setSearch('');
    setRolFiltro('');
  };

  useEffect(() => { fetchUsuarios(); }, [fetchUsuarios]);

  return (
    <ThemeProvider theme={theme}>
      <div>
        <AppBar position="static">
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Gestión de Usuarios</Typography>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => handleOpenDialog()}
              startIcon={<FontAwesomeIcon icon={faPlus} />}
              disabled={loading.action}
            >
              Nuevo Usuario
            </Button>
          </Toolbar>
        </AppBar>

        <BarraFiltros
          busqueda={search}
          onBusqueda={setSearch}
          placeholder="Buscar por nombre o correo"
          filtros={[
            {
              id: 'rol',
              tipo: 'chips',
              etiqueta: 'Rol',
              valor: rolFiltro,
              onChange: setRolFiltro,
              opciones: [
                { valor: '', etiqueta: 'Todos' },
                { valor: 'admin', etiqueta: 'Admin' },
                { valor: 'usuario', etiqueta: 'Usuario' },
              ],
            },
          ]}
          total={usuarios.length}
          mostrados={filteredUsuarios.length}
          onLimpiar={limpiarFiltros}
        />

        {loading.data ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>
        ) : (
          <TableContainer component={Paper} sx={{ margin: '20px auto', maxWidth: 1000 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Nombre</StyledTableCell>
                  <StyledTableCell>Correo</StyledTableCell>
                  <StyledTableCell>Rol</StyledTableCell>
                  <StyledTableCell>Imagen</StyledTableCell>
                  <StyledTableCell>Acciones</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsuarios.length > 0 ? filteredUsuarios.map(usuario => (
                  <TableRow key={usuario.id_usuario}>
                    <TableCell>{usuario.nombre}</TableCell>
                    <TableCell>{usuario.correo}</TableCell>
                    <TableCell>{usuario.es_admin ? 'Admin' : 'Usuario'}</TableCell>
                    <TableCell>
                      {usuario.imagen ? <img src={`${UPLOADS_URL}/uploads/${usuario.imagen}`} alt="usuario" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 5 }} /> : 'Sin imagen'}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<FontAwesomeIcon icon={faEdit} />}
                          onClick={() => handleOpenDialog(usuario)}
                          disabled={loading.action}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<FontAwesomeIcon icon={faTrash} />}
                          onClick={() => handleDeleteUsuario(usuario.id_usuario)}
                          disabled={loading.action}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow><TableCell colSpan={5} align="center">No se encontraron usuarios</TableCell></TableRow>
                )}
                {filteredUsuarios.length === 0 && (
                  <SinResultados colSpan={5} total={usuarios.length} onLimpiar={limpiarFiltros} entidad="usuarios" />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Dialog open={dialogOpen} onClose={handleCloseDialog} fullWidth maxWidth="sm">
          <DialogTitle>{editMode ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
          <DialogContent dividers>
            <TextField
              margin="dense"
              label="Nombre*"
              fullWidth
              value={newUsuario.nombre}
              onChange={e => setNewUsuario({ ...newUsuario, nombre: e.target.value })}
              error={!!errors.nombre}
              helperText={errors.nombre}
            />
            <TextField
              margin="dense"
              label="Correo*"
              fullWidth
              value={newUsuario.correo}
              onChange={e => setNewUsuario({ ...newUsuario, correo: e.target.value })}
              error={!!errors.correo}
              helperText={errors.correo}
            />
            <TextField
              margin="dense"
              label={editMode ? 'Nueva Contraseña' : 'Contraseña*'}
              fullWidth
              type="password"
              placeholder={editMode ? 'Dejar en blanco para mantener la actual' : ''}
              value={newUsuario.contrasena}
              onChange={e => setNewUsuario({ ...newUsuario, contrasena: e.target.value })}
              error={!!errors.contrasena}
              helperText={errors.contrasena}
            />

            <FormControl fullWidth margin="dense">
              <InputLabel>Rol</InputLabel>
              <Select
                value={newUsuario.es_admin ? 'Admin' : 'Usuario'}
                onChange={e => setNewUsuario({ ...newUsuario, es_admin: e.target.value === 'Admin' })}
              >
                <MenuItem value="Admin">Administrador</MenuItem>
                <MenuItem value="Usuario">Usuario</MenuItem>
              </Select>
            </FormControl>

            {/* Input de imagen con previsualización */}
            <Box mt={2}>
              <InputLabel>Imagen del usuario</InputLabel>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  setNewUsuario(prev => ({ ...prev, imagen: file }));
                }}
              />
              <Box mt={1}>
                {(newUsuario.imagen && typeof newUsuario.imagen !== 'string') ? (
                  // Imagen seleccionada nueva
                  <img
                    src={URL.createObjectURL(newUsuario.imagen)}
                    alt="Preview"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 5 }}
                  />
                ) : editMode && currentUsuario.imagen ? (
                  // Imagen actual del usuario
                  <img
                    src={`${UPLOADS_URL}/uploads/${currentUsuario.imagen}`}
                    alt="Actual"
                    style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 5 }}
                  />
                ) : null}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseDialog}
              color="inherit"
              disabled={loading.action}
            >
              Cancelar
            </Button>

            <Button
              onClick={handleSaveUsuario}
              variant="contained"
              color="primary"
              disabled={loading.action}
            >
              {loading.action
                ? <CircularProgress size={22} color="inherit" />
                : editMode ? 'Guardar Cambios' : 'Crear Usuario'}
            </Button>
          </DialogActions>


        </Dialog>
      </div>
    </ThemeProvider>
  );
};

export default UsuariosPage;
